import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * A modal built on the native <dialog> element, which traps focus,
 * closes on Escape and restores focus to the opener for free.
 */
export function Dialog({ open, onClose, title, description, children, className, labelledBy }) {
  const ref = useRef(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // Native dialogs focus the first control (the close button); let
      // the content pick a better starting point with data-autofocus.
      dialog.querySelector("[data-autofocus]")?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // A click on the backdrop lands on the <dialog> itself.
        if (e.target === ref.current) onClose();
      }}
      aria-labelledby={labelledBy || (title ? "dialog-title" : undefined)}
      className={cn(
        "w-[calc(100%-2rem)] max-w-md rounded-2xl border border-border bg-card p-0 text-foreground shadow-lg backdrop:bg-foreground/40",
        className
      )}
    >
      {open && (
        <div className="p-6">
          {title && (
            <div className="mb-3 flex items-start justify-between gap-4">
              <h2 id="dialog-title" className="font-display text-xl font-semibold leading-tight">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                className="-mr-2 -mt-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {description && <p className="mb-5 text-sm leading-relaxed text-muted-foreground">{description}</p>}
          {children}
        </div>
      )}
    </dialog>
  );
}
